import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";

const ExitIntentPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already shown in this session
    const alreadyShown = sessionStorage.getItem("exitIntentShown");
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves through top of page
      if (e.clientY <= 0 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
        sessionStorage.setItem("exitIntentShown", "true");
      }
    };

    // Also trigger on mobile when user scrolls up quickly (back gesture)
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY - 100 && currentScrollY < 50 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
        sessionStorage.setItem("exitIntentShown", "true");
      }
      lastScrollY = currentScrollY;
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasShown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await subscribe({ 
        email, 
        freebie_id: "101-protocols", 
        source: "exit-intent",
        tags: ["101-protocols-subscriber"]
      });
      toast.success("Check your email for your free protocol guide!");
      setShowPopup(false);
      // Redirect to bundle page after short delay
      setTimeout(() => {
        navigate("/glp-survival-system");
      }, 1500);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setShowPopup(false);
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-lg md:w-full z-50"
            data-testid="exit-intent-popup"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10"
                data-testid="close-popup-btn"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header with gradient */}
              <div className="bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] px-6 py-8 text-white text-center dark-section">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold mb-2">
                  Navigate your GLP-1 side effects
                </h2>
                <p className="text-white/80">
                  Don't leave without your free resource
                </p>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="text-center mb-6">
                  <p className="text-lg font-semibold text-[#1A2018] mb-2">
                    Get the FREE GLP-1 Protocol Guide
                  </p>
                  <p className="text-sm text-[#4B5548]">
                    Everything you need to know to start GLP-1 correctly — in one place
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-styled"
                    required
                    data-testid="exit-intent-email"
                  />
                  <button
                    type="submit"
                    className="btn-coral w-full justify-center"
                    disabled={loading}
                    data-testid="exit-intent-submit"
                  >
                    {loading ? "Sending..." : "Get Instant Access"}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>

                <p className="text-xs text-center text-[#838F80] mt-4">
                  Free educational download. No spam, ever.
                </p>

                <button
                  onClick={handleClose}
                  className="w-full text-center text-sm text-[#838F80] hover:text-[#4B5548] mt-4"
                >
                  No thanks, maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
